import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const ContentContext = createContext(null);

// Compute an absolute URL for image paths returned by the backend
function resolveAssetUrl(u) {
  if (!u) return u;
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return u.startsWith("/") ? `${base}${u}` : `${base}/${u}`;
}

// Turn any image URL into a circular PNG data-URL so admin-uploaded rectangular logos
// still render as a clean round icon in the browser tab.
function roundifyImageAsDataUrl(url) {
  return new Promise((resolve) => {
    if (typeof document === "undefined") return resolve(url);
    const size = 128;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext("2d");
        // clip circle
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        // white bg so transparency reads clean
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, size, size);
        // cover-fit the image
        const scale = Math.max(size / img.width, size / img.height);
        const dw = img.width * scale;
        const dh = img.height * scale;
        ctx.drawImage(img, (size - dw) / 2, (size - dh) / 2, dw, dh);
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        // Cross-origin taint or other → fall back to original url
        resolve(url);
      }
    };
    img.onerror = () => resolve(url);
    img.src = url;
  });
}

// Update <title> and favicon whenever brand info changes
async function applyBrandingToDocument(content) {
  if (typeof document === "undefined") return;
  const brand = content?.brand || {};
  const name = brand.name || "TMF";
  const tagline = brand.tagline || "by Silver Sure";
  const isContinuation = /^(by\s|for\s|[a-z])/i.test(tagline.trim());
  document.title = isContinuation ? `${name} ${tagline}` : `${name} — ${tagline}`;

  const logo = resolveAssetUrl(brand.logo_url);
  if (logo) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    // SVG logos are already vector, no need to roundify — they respect the SVG's shape.
    // Raster (PNG/JPEG) logos get canvas-clipped to a circle so the tab icon is a clean round badge.
    if (logo.endsWith(".svg")) {
      link.type = "image/svg+xml";
      link.href = logo;
    } else {
      const roundUrl = await roundifyImageAsDataUrl(logo);
      link.type = "image/png";
      link.href = roundUrl;
    }
  }
}

export function ContentProvider({ children }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      setContent(data);
    } catch (e) {
      console.warn("Content: failed to load site content", e);
      setContent({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { applyBrandingToDocument(content); }, [content]);

  const save = useCallback(async (next) => {
    const { data } = await api.put("/admin/content", { content: next });
    setContent(data.content);
    return data.content;
  }, []);

  const value = useMemo(
    () => ({ content, loading, reload: load, save }),
    [content, loading, load, save]
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used inside ContentProvider");
  return ctx;
}

/**
 * Utility to format currency according to site content settings.
 */
export function formatPrice(amount, content) {
  const symbol = content?.currency?.symbol || "₹";
  return `${symbol}${Number(amount).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
